import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import {
  REPORT_CONTENT_CURRENT_SCHEMA_VERSION,
  REPORT_CONTENT_HASH_VERSION,
  REPORT_CONTENT_INPUT_SCHEMA_VERSION,
  REPORT_CONTENT_REVISION_SCHEMA_VERSION,
  REPORT_CONTENT_SNAPSHOT_SCHEMA_VERSION,
  reportContentCurrentPointerV1Schema,
  reportContentInputV1Schema,
  reportContentRevisionReferenceV1Schema,
  reportContentSnapshotV1Schema,
  type ReportContentCurrentPointerV1,
  type ReportContentInputV1,
  type ReportContentReadinessV1,
  type ReportContentRevisionReferenceV1,
  type ReportContentSnapshotV1,
} from '../../types/reportContent';
import type { CustomBriefPreferences } from '../../types/siteRag';
import type { JsonValue } from '../../types/reportContractsV2';
import {
  canonicalJson,
  hashCanonicalJson,
} from '../reportGeneration/reportContractHash';
import {
  buildReportSedMaterialDependencyManifest,
  reportSedMaterialDependencyDrift,
  verifyReportSedMaterialDependencyManifest,
} from '../decisionAnalysis/sedContentIdentity';
import { providerAuthorityMaterialPayload } from '../decisionAnalysis/providerAuthorityMaterialIdentity';

function jsonValue(value: unknown): JsonValue {
  return JSON.parse(canonicalJson(value)) as JsonValue;
}

export function customBriefPreferencesFromSite(
  site: Pick<SiteEntity, 'typedPreferencesDefaults' | 'savedCustomBriefs'>,
): CustomBriefPreferences | null {
  const parsed = reportContentInputV1Schema.shape.customBrief.shape.snapshot.safeParse(
    site.typedPreferencesDefaults?.['custom-brief'],
  );
  if (parsed.success) return parsed.data;
  const saved = (site.savedCustomBriefs ?? []).flatMap(brief => {
    const candidate = reportContentInputV1Schema.shape.customBrief.shape.snapshot.safeParse(brief.preferences);
    return candidate.success ? [candidate.data] : [];
  });
  // A sole saved template is unambiguous. Multiple templates require the
  // actual selected request preferences; never silently choose another brief.
  return saved.length === 1 ? saved[0]! : null;
}

export function buildCustomBriefReportContentInputV1(input: {
  site: SiteEntity;
  sed: SiteEvidenceDocument;
  /** Actual validated request preferences, including a selected saved brief. */
  customBriefPreferences?: CustomBriefPreferences;
}): ReportContentInputV1 {
  const customBrief = input.customBriefPreferences === undefined
    ? customBriefPreferencesFromSite(input.site)
    : reportContentInputV1Schema.shape.customBrief.shape.snapshot.parse(input.customBriefPreferences);
  if (!customBrief) {
    throw new Error(
      'Custom Brief requires a saved name and at least one valid diligence question.',
    );
  }
  const siteSnapshot = jsonValue({
    name: input.site.name,
    address: input.site.address,
    location: input.site.location,
  });
  const projectIntentSnapshot = jsonValue(input.site.projectIntent ?? null);
  const savedByName = (input.site.savedCustomBriefs ?? []).filter(brief =>
    brief.name.trim().toLowerCase() === customBrief.briefName.trim().toLowerCase());
  const savedMatches = (input.site.savedCustomBriefs ?? [])
    .filter(brief => hashCanonicalJson(brief.preferences) === hashCanonicalJson(customBrief));
  if (savedMatches.length > 1 || (savedMatches.length === 0 && savedByName.length > 1)) {
    throw new Error('Custom Brief selection is ambiguous. Use a distinct saved brief name and questions.');
  }
  const savedBrief = savedMatches[0]
    ?? (savedByName.length === 1 ? savedByName[0] : undefined);
  const defaultPreferences = reportContentInputV1Schema.shape.customBrief.shape.snapshot.safeParse(
    input.site.typedPreferencesDefaults?.['custom-brief'],
  );
  const preferenceAuthority = savedBrief
    ? { kind: 'saved-brief', id: savedBrief.id }
    : defaultPreferences.success && hashCanonicalJson(defaultPreferences.data) === hashCanonicalJson(customBrief)
      ? { kind: 'site-default' }
      : { kind: 'request' };
  const evidenceSnapshot = jsonValue({
    customBriefPreferenceAuthority: preferenceAuthority,
    siteContext: input.site.siteContext ?? null,
    documents: input.site.documents ?? null,
    documentIds: input.site.documentIds ?? null,
    evidenceMode: input.site.evidenceMode ?? null,
    siteBoundary: input.site.siteBoundary ?? null,
    siteBoundaries: input.site.siteBoundaries ?? null,
    siteCentroid: input.site.siteCentroid ?? null,
    siteArea: input.site.siteArea ?? null,
    sitePerimeter: input.site.sitePerimeter ?? null,
    siteLayersRevision: input.site.siteLayersRevision ?? null,
    siteLayersCanonicalVersion:
      input.site.siteLayersCanonicalVersion ?? null,
    siteLayerCount: input.site.siteLayerCount ?? null,
    siteLayers: input.site.siteLayers ?? null,
    siteBoundaryAggregate: input.site.siteBoundaryAggregate ?? null,
    studyAreaScope: input.site.studyAreaScope ?? null,
    siteElevationRange: input.site.siteElevationRange ?? null,
    // These are ordinary prompt/source authorities, not derived returns.
    // Direct planning edits must invalidate content before an SED rebuild.
    financialAssumptions: input.site.financialAssumptions ?? null,
    supplyChainPlan: input.site.supplyChainPlan ?? null,
    generationAssessmentSelection: input.site.generationAssessmentSelection ?? null,
    generationProjectInputBinding: input.site.generationProjectInputBinding ?? null,
    gridLedStandbyPlanningSnapshot: input.site.gridLedStandbyPlanningSnapshot ?? null,
    promotedGenerationScenarioSnapshot: input.site.promotedGenerationScenarioSnapshot ?? null,
    generationImpactLayoutHead: input.site.generationImpactLayoutHead ?? null,
    maxPowerStudySnapshot: input.site.maxPowerStudySnapshot ?? null,
    pinnedPathwayComparisonId: input.site.pinnedPathwayComparisonId ?? null,
    economicContributionInputs: input.site.economicContributionInputs ?? null,
    economicContributionSnapshot: input.site.economicContributionSnapshot ?? null,
    economicProfileSnapshot: input.site.economicProfileSnapshot ?? null,
    economicResearchSnapshotPointer: input.site.economicResearchSnapshotPointer ?? null,
    auditOverrides: input.site.auditOverrides ?? null,
    demonstrationProvenance: input.site.demonstrationProvenance ?? null,
  });
  const customBriefSnapshot = jsonValue(customBrief);

  return reportContentInputV1Schema.parse({
    schemaVersion: REPORT_CONTENT_INPUT_SCHEMA_VERSION,
    hashVersion: REPORT_CONTENT_HASH_VERSION,
    siteId: input.site.id,
    productId: 'custom-brief',
    site: {
      contentHash: hashCanonicalJson(siteSnapshot),
      snapshot: siteSnapshot,
    },
    sed: {
      id: input.sed.id,
      version: input.sed.version,
      contentHash: hashCanonicalJson(input.sed),
      // Brief questions may use any full-SED field. Preserve that complete
      // evidence scope while excluding collection identity from currency.
      materialDependencies: buildReportSedMaterialDependencyManifest(input.sed, 'custom-brief', { fullDocument: true }),
    },
    projectIntent: {
      contentHash: hashCanonicalJson(projectIntentSnapshot),
      snapshot: projectIntentSnapshot,
    },
    customBrief: {
      contentHash: hashCanonicalJson(customBriefSnapshot),
      snapshot: customBriefSnapshot,
    },
    evidence: {
      contentHash: hashCanonicalJson(evidenceSnapshot),
      snapshot: evidenceSnapshot,
    },
  });
}

/** Rebuild the same preference authority, rather than substituting unrelated
 * site defaults for a saved-template or explicit immutable request. */
export function buildCurrentCustomBriefReportContentInputV1(input: {
  site: SiteEntity;
  sed: SiteEvidenceDocument;
  frozenInput: ReportContentInputV1;
}): ReportContentInputV1 {
  const evidence = input.frozenInput.evidence.snapshot;
  const authority = evidence && typeof evidence === 'object' && !Array.isArray(evidence)
    ? evidence.customBriefPreferenceAuthority : null;
  if (authority && typeof authority === 'object' && !Array.isArray(authority)
    && authority.kind === 'saved-brief') {
    const saved = input.site.savedCustomBriefs?.find(brief => brief.id === authority.id);
    if (!saved) throw new Error('The selected saved Custom Brief no longer exists.');
    const current = buildCustomBriefReportContentInputV1({
      site: input.site, sed: input.sed, customBriefPreferences: saved.preferences,
    });
    // Duplicate templates with identical questions do not replace the exact
    // source template selected by the frozen input.
    const currentEvidence = current.evidence.snapshot as Record<string, JsonValue>;
    currentEvidence.customBriefPreferenceAuthority = { kind: 'saved-brief', id: saved.id };
    current.evidence.contentHash = hashCanonicalJson(currentEvidence);
    return reportContentInputV1Schema.parse(current);
  }
  if (authority && typeof authority === 'object' && !Array.isArray(authority)
    && authority.kind === 'request') {
    return buildCustomBriefReportContentInputV1({
      site: input.site, sed: input.sed,
      customBriefPreferences: input.frozenInput.customBrief.snapshot,
    });
  }
  return buildCustomBriefReportContentInputV1({ site: input.site, sed: input.sed });
}

export function hashReportContentInputV1(
  input: ReportContentInputV1,
): string {
  return hashCanonicalJson(reportContentInputV1Schema.parse(input));
}

/** No regeneration is needed when the complete consumed evidence still
 * matches. Source hashes and collection versions remain immutable provenance. */
export function reportContentInputsCurrent(
  frozenValue: ReportContentInputV1,
  liveValue: ReportContentInputV1,
): boolean {
  const frozen = reportContentInputV1Schema.safeParse(frozenValue);
  const live = reportContentInputV1Schema.safeParse(liveValue);
  if (!frozen.success || !live.success) return false;
  const bound = frozen.data.sed.materialDependencies;
  const current = live.data.sed.materialDependencies;
  if (bound) {
    if (!current || bound.scope !== 'full-document' || current.scope !== 'full-document'
      || !verifyReportSedMaterialDependencyManifest(bound, 'custom-brief')
      || !verifyReportSedMaterialDependencyManifest(current, 'custom-brief')) return false;
    // Keep original authority hashes immutable. Only known provider/UI
    // bookkeeping is excluded from currency; planning and source facts stay.
    if (frozen.data.evidence.contentHash !== hashCanonicalJson(frozen.data.evidence.snapshot)
      || live.data.evidence.contentHash !== hashCanonicalJson(live.data.evidence.snapshot)) return false;
    if (hashCanonicalJson({ ...frozen.data, sed: null, evidence: null })
      !== hashCanonicalJson({ ...live.data, sed: null, evidence: null })
      || hashCanonicalJson(providerAuthorityMaterialPayload(frozen.data.evidence.snapshot))
        !== hashCanonicalJson(providerAuthorityMaterialPayload(live.data.evidence.snapshot))) return false;
    return reportSedMaterialDependencyDrift(bound, current).current;
  }
  // A legacy raw hash cannot reconstruct the evidence it represents.
  return hashCanonicalJson({ ...frozen.data, sed: null })
      === hashCanonicalJson({ ...live.data, sed: null })
    && frozen.data.sed.id === live.data.sed.id
    && frozen.data.sed.version === live.data.sed.version
    && frozen.data.sed.contentHash === live.data.sed.contentHash;
}

function reportContentSnapshotIdentityPayload(
  snapshot: Omit<ReportContentSnapshotV1, 'contentRevisionId'>,
): Omit<ReportContentSnapshotV1, 'contentRevisionId'> {
  return snapshot;
}

export function buildReportContentSnapshotV1(input: {
  sourceInput: ReportContentInputV1;
  reportId: string;
  reportRevisionId: string;
  contentHash: string;
  createdAt: string;
}): ReportContentSnapshotV1 {
  const sourceInput = reportContentInputV1Schema.parse(input.sourceInput);
  const payload = reportContentSnapshotIdentityPayload({
    schemaVersion: REPORT_CONTENT_SNAPSHOT_SCHEMA_VERSION,
    siteId: sourceInput.siteId,
    productId: sourceInput.productId,
    reportId: input.reportId,
    reportRevisionId: input.reportRevisionId,
    input: sourceInput,
    inputHash: hashReportContentInputV1(sourceInput),
    contentHash: input.contentHash,
    createdAt: input.createdAt,
  });
  const identityHash = hashCanonicalJson(payload);
  return reportContentSnapshotV1Schema.parse({
    ...payload,
    contentRevisionId: `report-content-${identityHash.slice(0, 48)}`,
  });
}

export function verifyReportContentSnapshotIdentity(
  value: unknown,
): value is ReportContentSnapshotV1 {
  const parsed = reportContentSnapshotV1Schema.safeParse(value);
  if (!parsed.success) return false;
  const snapshot = parsed.data;
  const { contentRevisionId: _contentRevisionId, ...payload } = snapshot;
  return snapshot.inputHash === hashReportContentInputV1(snapshot.input)
    && snapshot.contentRevisionId
      === `report-content-${hashCanonicalJson(payload).slice(0, 48)}`;
}

export function buildReportContentRevisionReferenceV1(input: {
  snapshot: ReportContentSnapshotV1;
  snapshotPath: string;
  readiness: ReportContentReadinessV1;
}): ReportContentRevisionReferenceV1 {
  const snapshot = reportContentSnapshotV1Schema.parse(input.snapshot);
  if (!verifyReportContentSnapshotIdentity(snapshot)) {
    throw new Error('Report-content snapshot identity is invalid.');
  }
  return reportContentRevisionReferenceV1Schema.parse({
    schemaVersion: REPORT_CONTENT_REVISION_SCHEMA_VERSION,
    contentRevisionId: snapshot.contentRevisionId,
    siteId: snapshot.siteId,
    productId: snapshot.productId,
    reportId: snapshot.reportId,
    reportRevisionId: snapshot.reportRevisionId,
    inputHash: snapshot.inputHash,
    contentHash: snapshot.contentHash,
    snapshotPath: input.snapshotPath,
    createdAt: snapshot.createdAt,
    readiness: input.readiness,
  });
}

export function buildReportContentCurrentPointerV1(input: {
  siteId: string;
  reference?: ReportContentRevisionReferenceV1;
  latestAttemptReference?: ReportContentRevisionReferenceV1;
  refreshState: ReportContentCurrentPointerV1['refreshState'];
  refreshInputHash: string | null;
  refreshJobId: string | null;
  refreshRequestedAt?: string;
  refreshUpdatedAt: string;
  refreshReason: string | null;
}): ReportContentCurrentPointerV1 {
  return reportContentCurrentPointerV1Schema.parse({
    schemaVersion: REPORT_CONTENT_CURRENT_SCHEMA_VERSION,
    siteId: input.siteId,
    productId: 'custom-brief',
    ...(input.reference ? { reference: input.reference } : {}),
    ...(input.latestAttemptReference
      ? { latestAttemptReference: input.latestAttemptReference }
      : {}),
    refreshState: input.refreshState,
    refreshInputHash: input.refreshInputHash,
    refreshJobId: input.refreshJobId,
    ...(input.refreshRequestedAt
      ? { refreshRequestedAt: input.refreshRequestedAt }
      : {}),
    refreshUpdatedAt: input.refreshUpdatedAt,
    refreshReason: input.refreshReason,
    updatedAt: input.refreshUpdatedAt,
  });
}

export function reportContentInputMatchesCurrentSite(input: {
  frozenInput: ReportContentInputV1;
  liveSite: SiteEntity;
  frozenSed: SiteEvidenceDocument;
}): boolean {
  if (
    input.liveSite.sedId !== input.frozenSed.id
    || input.liveSite.sedVersion !== input.frozenSed.version
  ) return false;
  try {
    const current = buildCurrentCustomBriefReportContentInputV1({
      site: input.liveSite,
      sed: input.frozenSed,
      frozenInput: input.frozenInput,
    });
    return reportContentInputsCurrent(input.frozenInput, current);
  } catch {
    return false;
  }
}

export function reportContentJobKey(input: {
  siteId: string;
  inputHash: string;
  /** Explicit gestures retry failed/obsolete same-input Briefs without
   * colliding with a terminal automatic content-addressed receipt. */
  retryGestureKey?: string;
}): string {
  if (!/^[a-f0-9]{64}$/.test(input.inputHash)) {
    throw new Error('Report-content inputHash must be a canonical SHA-256 hash.');
  }
  const digest = hashCanonicalJson({
    schemaVersion: REPORT_CONTENT_INPUT_SCHEMA_VERSION,
    siteId: input.siteId,
    productId: 'custom-brief',
    inputHash: input.inputHash,
    ...(input.retryGestureKey ? { retryGestureKey: input.retryGestureKey } : {}),
  });
  return `report-content-custom-brief-${digest.slice(0, 36)}`;
}
